import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { resolveApiUrl } from '../utils/resolveApiUrl';

export const useAxios = () => {
  const [requestStatus, setRequestStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState({
    isLoading: false,
    message: '',
  });
  const cancelTokenSources = useRef([]);

  const clearRequestStatus = () => {
    setRequestStatus(null);
  };

  useEffect(() => {
    return () => {
      cancelTokenSources.current.forEach((source) => source.cancel());
    };
  }, []);

  const sendRequest = useCallback(
    async (url, method = 'GET', body = null, headers = {}, loadingMessage = '') => {
      setLoadingStatus({
        isLoading: true,
        message: loadingMessage,
      });

      try {
        const cancelTokenSource = axios.CancelToken.source();
        cancelTokenSources.current.push(cancelTokenSource);

        const response = await axios({
          baseURL: resolveApiUrl(),
          url,
          method,
          data: body,
          headers: (() => {
            const nextHeaders = { ...headers };
            delete nextHeaders.Authorization;
            delete nextHeaders.authorization;
            return nextHeaders;
          })(),
          withCredentials: true,
          cancelToken: cancelTokenSource.token,
        });

        cancelTokenSources.current = cancelTokenSources.current.filter(
          (source) => source !== cancelTokenSource
        );

        setLoadingStatus({ isLoading: false, message: '' });

        if (response.data.status && response.data.status.isShown) {
          setRequestStatus({
            message: response.data.status.message,
            severity: response.data.status.severity,
            duration: response.data.status.duration,
          });
        }

        return response.data;
      } catch (error) {
        setLoadingStatus({ isLoading: false, message: '' });

        if (axios.isCancel(error)) {
          return;
        }

        if (error.response?.data) {
          if (error.response.data.status) {
            setRequestStatus({
              message: error.response.data.status.message,
              severity: error.response.data.status.severity,
              duration: error.response.data.status.duration,
            });
          } else if (error.response.data.message) {
            setRequestStatus({
              message: error.response.data.message,
              severity: 'error',
              duration: 6000,
            });
          }
        } else if (error.message) {
          setRequestStatus({
            message: 'Chyba připojení k serveru',
            severity: 'error',
            duration: 6000,
          });
        }

        throw error;
      }
    },
    []
  );

  return {
    requestStatus,
    loadingStatus,
    sendRequest,
    clearRequestStatus,
  };
};
